import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import FullPageLayout from "../../components/FullPageLayout/FullPageLayout";
import { auth } from "../../firebase";
import { useAppAuthState } from "../../hooks/use-app-auth-state";
import { createUser } from "../../services/userService";
import { SitePage } from "../../types/SitePage";
import "./RegisterPage.scss";

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { unverifiedFireUser, isAuthLoading, isLoggedIn } = useAppAuthState();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isAuthLoading && isLoggedIn) {
      // If user is already logged in and verified, redirect to app
      navigate("/home");
    } else if (
      !isAuthLoading &&
      unverifiedFireUser &&
      !unverifiedFireUser.emailVerified
    ) {
      // If user exists but not verified, redirect to verify email page
      navigate("/verify-email");
    }
  }, [unverifiedFireUser, isLoggedIn, isAuthLoading, navigate]);

  const handleRegister = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      if (userCredential.user) {
        await createUser(userCredential.user);
        await sendEmailVerification(userCredential.user);
        // Don't set success message, directly navigate to verify page
        // The VerifyEmailPage will show relevant info.
        navigate("/verify-email");
      } else {
        setError("Failed to create user. Please try again.");
      }
    } catch (err: any) {
      if (err.code === "auth/email-already-in-use") {
        setError("This email address is already in use.");
      } else if (err.code === "auth/weak-password") {
        setError(
          "Password is too weak. It should be at least 6 characters long.",
        );
      } else {
        setError("Failed to register. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isAuthLoading || (unverifiedFireUser && !isAuthLoading)) {
    // Show loading spinner while auth state is resolving or if navigating
    return (
      <FullPageLayout page={SitePage.Register} title="Register">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </FullPageLayout>
    );
  }

  return (
    <FullPageLayout page={SitePage.Register} title="Register">
      <h2>Register</h2>
      <Form onSubmit={handleRegister} className="register-form">
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-3" controlId="formRegisterEmail">
          <Form.Label>Email address</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="formRegisterPassword">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="formRegisterConfirmPassword">
          <Form.Label>Confirm Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </Form.Group>

        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              Registering...
            </>
          ) : (
            "Register"
          )}
        </Button>
      </Form>
    </FullPageLayout>
  );
};

export default RegisterPage;
